import type { CareScenarioChange } from './careScenarioChange';

export const CARE_AMOUNTS = ['half', 'estimate', 'double'] as const;
export const FAMILY_HELP_OPTIONS = ['none', 'estimate', 'all'] as const;

export type CareAmount = (typeof CARE_AMOUNTS)[number];
export type FamilyHelp = (typeof FAMILY_HELP_OPTIONS)[number];

/** The two assumptions a consumer may override; 'estimate' keeps the model's prediction. */
export interface CareAdjustments {
  careAmount: CareAmount;
  familyHelp: FamilyHelp;
}

export const ESTIMATED_CARE: CareAdjustments = { careAmount: 'estimate', familyHelp: 'estimate' };

const CARE_HOURS_MULTIPLIERS: Record<CareAmount, number | null> = { half: 0.5, estimate: null, double: 2 };
const FAMILY_CARE_SHARES: Record<FamilyHelp, number | null> = { none: 0, estimate: null, all: 1 };

function keyFor<T extends string>(values: Record<T, number | null>, value: number | undefined): T {
  const match = (Object.keys(values) as T[]).find(key => values[key] === (value ?? null));
  return match ?? ('estimate' as T);
}

/** The adjustments saved on the client; values set any other way read as the estimate. */
export function careAdjustmentsOf(client: Pick<Client, 'clientCustomInferences'>): CareAdjustments {
  const { careHoursMultiplier, familyCareShare } = client.clientCustomInferences ?? {};
  return {
    careAmount: keyFor(CARE_HOURS_MULTIPLIERS, careHoursMultiplier),
    familyHelp: keyFor(FAMILY_CARE_SHARES, familyCareShare),
  };
}

export function careAdjustmentsChange({ careAmount, familyHelp }: CareAdjustments): CareScenarioChange {
  return {
    careHoursMultiplier: CARE_HOURS_MULTIPLIERS[careAmount],
    familyCareShare: FAMILY_CARE_SHARES[familyHelp],
  };
}

export function isCustomCare({ careAmount, familyHelp }: CareAdjustments): boolean {
  return careAmount !== 'estimate' || familyHelp !== 'estimate';
}
